// AuthGate
// ─────────────────────────────────────────────────────────────────────
// Subscribes to Firebase onAuthStateChanged once and pushes the resulting
// {user, loading, isAuthorized} into the AuthContext. Used at the router
// root so every page (except /login) can rely on `useAuth()` returning a
// resolved value.
//
// Authorization is the *client UX* check (email must end in
// @commonequity.com). The *real* gate is server-side — the backend's
// auth/middleware.ts verifies the same fact plus an admin_users allow-list
// row. We sign out unauthorized accounts here so a leftover Google session
// on a wrong account doesn't sit half-authenticated.

import { useEffect, useState, type ReactNode } from 'react';
import {
  auth,
  clearTokenCache,
  isWorkspaceEmail,
  onAuthStateChanged,
  signOut,
  type User,
} from '@/lib/auth';
import { AuthContext, type AuthContextValue } from '@/hooks/useAuth';

export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthContextValue>({
    user: null,
    loading: true,
    isAuthorized: false,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (!user) {
        clearTokenCache();
        setState({ user: null, loading: false, isAuthorized: false });
        return;
      }
      const ok = isWorkspaceEmail(user.email);
      if (!ok) {
        // Wrong workspace — sign out and surface "domain not allowed".
        try {
          await signOut();
        } catch {
          /* ignore */
        }
        clearTokenCache();
        // Bounce through login with a reason flag the login page reads.
        if (
          typeof window !== 'undefined' &&
          !window.location.pathname.startsWith('/login')
        ) {
          window.location.href = '/login?reason=wrong_domain';
        }
        setState({ user: null, loading: false, isAuthorized: false });
        return;
      }
      setState({ user, loading: false, isAuthorized: true });
    });
    return () => unsubscribe();
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
